import type { ButtonProps } from '@latitude/button';
import { Button } from '@latitude/button';
import { Separator } from '@latitude/separator';
import { Text } from '@latitude/text';
import { cn } from '@latitude/token-utils';
import { Tooltip, TooltipProvider } from '@latitude/tooltip';
import type { Ref } from 'react';
import type { ButtonAction, FooterProps } from 'types';

import { footerVariants, leadingVariants, trailingVariants } from './variants';

type ActionSlot = 'leading' | 'primary' | 'secondary' | 'tertiary';

const ActionButton = ({
  action,
  type,
  variant,
  slot,
  testID,
  withDivider = false,
}: {
  action?: ButtonAction;
  type: ButtonProps['type'];
  variant: ButtonProps['variant'];
  slot: ActionSlot;
  testID: string;
  withDivider?: boolean;
}) => {
  if (!action) {
    return null;
  }
  const { label, tooltip, ...buttonProps } = action;

  const button = (
    <>
      <Button
        data-testid={`${testID}-action-${slot}`}
        type={type}
        htmlType="button"
        variant={variant}
        size="medium"
        {...buttonProps}
      >
        {label}
      </Button>
      {withDivider && (
        // self-stretch, because the row is `items-center`: an empty span would
        // otherwise collapse to zero height and take its right border with it.
        <span className="border-r-border-sys-border-1 border-color-ref-ui-divider-strong self-stretch" />
      )}
    </>
  );

  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip text={tooltip}>{button}</Tooltip>
      </TooltipProvider>
    );
  }
  return button;
};

/**
 * The shared footer for Latitude surfaces: modals, side panels, and cards.
 * 72px tall, with a top separator, an optional leading action and description
 * on the left, and the tertiary/secondary/primary action group on the right.
 *
 * The trailing group is tied to `primaryAction`: secondary and tertiary do not
 * render without it. That is a Latitude rule, not an oversight.
 */
const Footer = ({
  leadingAction,
  description,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  className,
  'data-testid': testID = 'footer',
  ref,
}: FooterProps & {
  ref?: Ref<HTMLDivElement>;
}) => (
  <div
    ref={ref}
    data-testid={testID}
    className={cn(footerVariants(), className)}
  >
    <Separator
      orientation="horizontal"
      className="absolute left-0 right-0 top-0"
      data-testid={`${testID}-separator`}
    />

    <div className={leadingVariants()}>
      <ActionButton
        action={leadingAction}
        slot="leading"
        type="ghost"
        variant="neutral"
        testID={testID}
      />
      {description != null && (
        <div className="min-w-0 flex-1" data-testid={`${testID}-description`}>
          {typeof description === 'string' ? (
            <Text variant="bodyRegular" color="muted">
              {description}
            </Text>
          ) : (
            description
          )}
        </div>
      )}
    </div>

    <div className={trailingVariants()}>
      {primaryAction && secondaryAction && (
        <ActionButton
          action={tertiaryAction}
          slot="tertiary"
          type="ghost"
          variant="primary"
          testID={testID}
          withDivider
        />
      )}
      {primaryAction && (
        <ActionButton
          action={secondaryAction}
          slot="secondary"
          type="outline"
          variant="neutral"
          testID={testID}
        />
      )}
      {/* `primary`, not `strong`: the strong-then-primary progression belongs to
          multi-step flows, where `strong` carries "Next" and `primary` is saved
          for the step that completes the flow. A footer is single-step unless
          told otherwise, so it defaults to the completing colour and a wizard
          passes `strong` for its intermediate steps. */}
      <ActionButton
        action={primaryAction}
        slot="primary"
        type="filled"
        variant="primary"
        testID={testID}
      />
    </div>
  </div>
);

Footer.displayName = 'Footer';

export { Footer, type ButtonAction, type FooterProps };
